import type { ReactNode } from 'react'
import { cn } from '@/utils/cn'
import { HINT, PANEL, PANEL_SUBTITLE, PANEL_TITLE } from './tokens'

/**
 * The one panel every section on this page is built from: an 18px white card with a heading row,
 * an optional one-line description, an optional right-hand action slot, and a body.
 *
 * A single primitive rather than each card assembling its own header — that is what keeps eight
 * unrelated ops sections reading as one page, and it is the reason none of them nests a card
 * inside a card.
 */
export function AdminPanel({
  title,
  description,
  badge,
  actions,
  footnote,
  children,
  className,
}: {
  title: string
  description?: ReactNode
  /** Status that qualifies the whole panel, e.g. the Zocdoc environment or a phase marker. */
  badge?: ReactNode
  /** Controls that belong to the panel as a whole — filters, refresh, a primary action. */
  actions?: ReactNode
  /** Caveat that applies to everything above it. Rendered under a hairline, in the faint tone. */
  footnote?: ReactNode
  children?: ReactNode
  className?: string
}) {
  return (
    <section className={cn([PANEL, 'px-[22px] pt-5 pb-6', className])}>
      <header
        className={cn([
          'flex flex-wrap items-end justify-between gap-x-6 gap-y-3',
          // The rule only earns its place when something follows it.
          (description || children) && 'mb-4',
        ])}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className={PANEL_TITLE}>{title}</h2>
            {badge}
          </div>
          {description && (
            <p className={cn([PANEL_SUBTITLE, 'mt-1 mb-0 max-w-[72ch]'])}>{description}</p>
          )}
        </div>
        {actions && <div className="flex flex-wrap items-end gap-2.5">{actions}</div>}
      </header>

      {children}

      {footnote && (
        <p className={cn([HINT, 'mt-4 mb-0 border-t border-[#ECEEF1] pt-3'])}>{footnote}</p>
      )}
    </section>
  )
}
